/**
 * Consistent unit counts (total / occupied / vacant) across landlord, agent, and BnB dashboards.
 * When a units array is present, it is the single source of truth so totals always match detail views.
 */

export function getUnitCanonicalId(unit) {
  if (!unit) return '';
  return String(unit.id ?? unit.unitId ?? unit.uuid ?? '').trim();
}

export function parseUnitsFromApiResponse(unitsResponse) {
  if (!unitsResponse) return [];
  const payload = unitsResponse?.data ?? unitsResponse;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.units)) return payload.units;
  if (Array.isArray(payload?.data)) return payload.data;
  if (unitsResponse.status === 'success' && Array.isArray(unitsResponse.data)) {
    return unitsResponse.data;
  }
  return [];
}

function unitHasTenant(unit) {
  const tenantName = unit?.tenantName != null ? String(unit.tenantName).trim() : '';
  const tenantId = unit?.tenantId != null ? String(unit.tenantId).trim() : '';
  return Boolean(tenantName || tenantId);
}

function isRentalUnitOccupied(unit) {
  return unitHasTenant(unit);
}

function isBnbUnitOccupied(unit, bnbOccupiedUnitIds) {
  const unitId = getUnitCanonicalId(unit);
  if (unitId && bnbOccupiedUnitIds instanceof Set && bnbOccupiedUnitIds.has(unitId)) {
    return true;
  }
  if (unitHasTenant(unit)) return true;
  return unit?.isAvailable === false;
}

/**
 * @param {Object} property - Property object (may include units[])
 * @param {{ propertyScope?: string, bnbOccupiedUnitIds?: Set<string> }} [options]
 * @returns {{ totalUnits: number, occupiedCount: number, vacantCount: number, damagedCount: number }}
 */
export function computePropertyUnitStats(property, options = {}) {
  const { propertyScope = 'landlord', bnbOccupiedUnitIds = null } = options;
  const isBnb = String(propertyScope).toLowerCase() === 'bnb';
  const units = Array.isArray(property?.units) ? property.units : [];
  const damagedCount = units.filter(
    (u) => String(u?.status || '').toLowerCase() === 'damaged'
  ).length;

  if (units.length > 0) {
    let occupiedCount;
    if (isBnb) {
      occupiedCount = units.filter((u) => isBnbUnitOccupied(u, bnbOccupiedUnitIds)).length;
    } else {
      occupiedCount = units.filter(isRentalUnitOccupied).length;
    }
    const totalUnits = units.length;
    const vacantCount = Math.max(0, totalUnits - occupiedCount);
    return { totalUnits, occupiedCount, vacantCount, damagedCount };
  }

  const totalUnitsFromApi = Number(property?.unitCount ?? property?.totalUnits ?? 0);
  const totalUnits = Number.isFinite(totalUnitsFromApi) && totalUnitsFromApi >= 0
    ? totalUnitsFromApi
    : 0;

  let occupiedCount = 0;
  if (isBnb && bnbOccupiedUnitIds instanceof Set && bnbOccupiedUnitIds.size > 0) {
    occupiedCount = bnbOccupiedUnitIds.size;
  } else {
    const occupiedFromApi = Number(
      property?.occupiedUnits ?? property?.occupiedCount ?? property?.totalOccupied ?? 0
    );
    occupiedCount = Number.isFinite(occupiedFromApi) && occupiedFromApi >= 0 ? occupiedFromApi : 0;
  }

  if (occupiedCount > totalUnits) occupiedCount = totalUnits;
  const vacantCount = Math.max(0, totalUnits - occupiedCount);

  return { totalUnits, occupiedCount, vacantCount, damagedCount };
}
